/** @jsxImportSource @emotion/react */

import { css } from "@emotion/react";

import { SyntheticEvent, useEffect, useState } from "react";

import { useTranslation } from "react-i18next";

import { noop } from "lodash";
import Autocomplete from "@/components/Autocomplete/Autocomplete";
import Dialog from "@/components/Dialog/Dialog";
import { DialogWidth } from "@/components/Dialog/types/enums";
import {
  ANALYSIS_PROVIDER_OPTIONS,
  ANALYSIS_PROVIDER_OPTIONS_PROD
} from "@/Pages/Integrations/config";
import {
  ANALYSIS_PROVIDER_NAMES,
  INTEGRATION_TYPE
} from "@/Pages/Integrations/types";
import { USER_PERMISSIONS } from "@/roles/types/enums";
import { inputFieldMarginBottom, useMediaQueries } from "@/shared";
import DialogActions from "@/shared/components/buttons/DialogActions";
import { getEnvironment } from "@/shared/functions/functions";
import { useIntegrationsDialogStore } from "@/stores/useIntegrationsDialogStore";
import { getAccessStatus } from "@/stores/useUserDataStore";
import Typography from "@mui/material/Typography";

import { colors } from "@dotnet/telematics-design-system";

import {
  IntegrationTypeDialogProps,
  IntegrationTypeOption,
  ProviderTypeOption
} from "./types";

const IntegrationTypeDialog = ({
  isOpen,
  onClose
}: IntegrationTypeDialogProps) => {
  const { t } = useTranslation();
  const { toMd } = useMediaQueries();

  const { isProductionEnv } = getEnvironment();

  const canViewAnalysisProviders = getAccessStatus(
    USER_PERMISSIONS.SeeAnalysisProviders
  );

  const initialIntegrationType = useIntegrationsDialogStore(
    state => state.initialIntegrationType
  );
  const goToSftpForm = useIntegrationsDialogStore(state => state.goToSftpForm);
  const goToApiForm = useIntegrationsDialogStore(state => state.goToApiForm);
  const goToConveyIntegrationsForm = useIntegrationsDialogStore(
    state => state.goToConveyIntegrationsForm
  );
  const goToVDOIntegrationsForm = useIntegrationsDialogStore(
    state => state.goToVDOIntegrationsForm
  );

  const typeOptions: IntegrationTypeOption[] = [
    { value: INTEGRATION_TYPE.Sftp, name: t("Tabs##sftp") },
    { value: INTEGRATION_TYPE.Api, name: t("Tabs##api") },
    ...(canViewAnalysisProviders
      ? [
          {
            value: INTEGRATION_TYPE.AnalysisProvider,
            name: t("Tabs##analysis providers")
          }
        ]
      : [])
  ];

  const getOptionForType = (type: INTEGRATION_TYPE | null) =>
    typeOptions.find(option => option.value === type) ?? null;

  const [selectedType, setSelectedType] =
    useState<IntegrationTypeOption | null>(
      getOptionForType(initialIntegrationType)
    );

  const [analysisProvider, setAnalysisProvider] = useState<ProviderTypeOption>(
    ANALYSIS_PROVIDER_OPTIONS[0]
  );

  useEffect(() => {
    if (isOpen) {
      setSelectedType(getOptionForType(initialIntegrationType));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, initialIntegrationType]);

  const handleChange = (_: SyntheticEvent, value: IntegrationTypeOption) => {
    setSelectedType(value);
  };

  const handleProviderChange = (
    _: SyntheticEvent,
    value: ProviderTypeOption
  ) => {
    setAnalysisProvider(value);
  };

  const handleNext = () => {
    switch (selectedType?.value) {
      case INTEGRATION_TYPE.Sftp:
        goToSftpForm();
        break;

      case INTEGRATION_TYPE.Api:
        goToApiForm();
        break;

      case INTEGRATION_TYPE.AnalysisProvider:
        analysisProvider.id === ANALYSIS_PROVIDER_NAMES.Convey
          ? goToConveyIntegrationsForm()
          : goToVDOIntegrationsForm();
        break;

      default:
        return;
    }
  };

  const resetLocalState = () => {
    setSelectedType(null);
  };

  const isAnalysisProviders =
    selectedType?.value === INTEGRATION_TYPE.AnalysisProvider;

  const providerOptions = isProductionEnv
    ? ANALYSIS_PROVIDER_OPTIONS_PROD
    : ANALYSIS_PROVIDER_OPTIONS;

  return (
    <Dialog
      data-testid="integration-type-dialog"
      title={t("Dialog##add new integration##title")}
      description={t("Dialog##add new integration##description")}
      extendToMaxWidth={toMd}
      isTitleSeparator
      isActionsSeparator
      open={isOpen}
      close={onClose}
      submit={handleNext}
      width={toMd ? undefined : DialogWidth.MinWidth}
      slotProps={{ transition: { onExited: resetLocalState } }}
      actions={
        <DialogActions
          onClose={onClose}
          isLoading={false}
          isreadyToConfirm={Boolean(selectedType)}
          confirmationButtonText={t("Button##next")}
          confirmationButtonTestId="integration-type-next-button"
          cancelButtonText={t("Button##close")}
          cancelButtonTestId="integration-type-close-button"
        />
      }
    >
      <Autocomplete
        data-testid="integration-type-dropdown"
        size="medium"
        fullWidth
        disableClearable
        isRequired
        labelLeft={t("Dialog##integration type")}
        value={selectedType}
        options={typeOptions}
        getOptionLabel={option => option?.name ?? ""}
        isOptionEqualToValue={(option, value) => option?.value === value?.value}
        onChange={handleChange}
        textFieldParams={{ placeholder: t("Dialog##select") }}
        onBlur={noop}
        customStyle={isAnalysisProviders && inputFieldMarginBottom}
      />

      {selectedType?.value === INTEGRATION_TYPE.AnalysisProvider && (
        <Autocomplete
          data-testid="integration-provider-dropdown"
          size="medium"
          fullWidth
          disableClearable
          isRequired
          labelLeft={t("Dialog##provider")}
          value={analysisProvider}
          options={providerOptions}
          getOptionLabel={option => option?.name ?? ""}
          isOptionEqualToValue={(option, value) =>
            option?.value === value?.value
          }
          renderOption={(propsData, { id, name, description }) => {
            return (
              <li {...propsData} key={id}>
                <div
                  css={css({
                    display: "flex",
                    flexDirection: "column"
                  })}
                >
                  <Typography
                    variant="body2Medium"
                    sx={{ color: colors.textDark }}
                  >
                    {name}
                  </Typography>

                  <Typography
                    variant="body3Regular"
                    sx={{ color: colors.textSecondary }}
                  >
                    {description}
                  </Typography>
                </div>
              </li>
            );
          }}
          onChange={handleProviderChange}
          textFieldParams={{ placeholder: t("Dialog##select") }}
          onBlur={noop}
        />
      )}
    </Dialog>
  );
};

export default IntegrationTypeDialog;
